import "../css/FileUpload.css";

import clsx from "clsx";
import React, { Component } from "react";
import Dropzone, { type FileWithPreview } from "react-dropzone";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import NotificationSystem from "react-notification-system";
import { type ConnectedProps, connect } from "react-redux";

import { handleDropped } from "~/actions/dashboard";
import rotateLeft from "~/assets/img/rotateLeft.svg";
import rotateRight from "~/assets/img/rotateRight.svg";
import { convertHeicFiles, isHeicFile } from "~/common/heic";
import { remoteLog } from "~/common/logging";
import { hasUnfilledCorrection, isPatient } from "~/common/patient";
import { createTrailingDebounce } from "~/common/trailing-debounce";
import { redesignedPhotoPlaceholder } from "~/components/patient/patient_new/photo-placeholders";
import { Img } from "~/components/ui/image";
import { API_PATIENT_IMAGE } from "~/config";
import en from "~/locale/en";
import ru from "~/locale/ru";
import type { RootState } from "~/store";
import type { TCorrectionMediaSemantics, TCorrectionMediaView } from "~/types/patient";

const DROP_ZONE_WIDTH = 180;
const DROP_ZONE_HEIGHT = 135;
// Bound canvas memory without reducing ordinary high-resolution phone photos below 4000px.
const MAX_BROAD_SIDE_WIDTH = 4000;

const mapStateToProps = (state: RootState, props: _FileUploadProps) => {
  return {
    patient: state.patient,
    locale: state.intl.locale,
    file: state.media[props.id as TCorrectionMediaSemantics],
  };
};

const mapDispatchToProps = {
  handleDropped,
};

type _FileUploadProps = {
  id: TCorrectionMediaSemantics | `recipe_${number}`;
  semantics: TCorrectionMediaSemantics | "recipe";
  preview?: boolean;
  correctionNum?: number;
  onFileUploadEnd?(): void;
  maxFileSize?: number;
  patient_id: number;
  ariaLabelledBy: string;
  /** Overrides the progress bar's default teal (#00858A) — e.g. power cap's dark #524B46. */
  progressBarColor?: string;
  /**
   * Skip the "already has this photo on file" preload entirely — even when no `correctionNum` is
   * passed. Ordering an add-on service (e.g. power cap) for an already-treated patient reuses this
   * component for a brand-new upload, not a continuation of their existing protocol, so the
   * `required_images` fallback below (meant for editing/re-submitting THAT protocol) would otherwise
   * show their real historical photos as if already uploaded here.
   */
  ignoreExistingMedia?: boolean;
  multiple?: boolean;
  nextBatchId?(offset: number): `recipe_${number}`;
};

type FileUploadProps = PropsFromRedux & _FileUploadProps;

type DropZoneItem = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | null;
  name: string;
  image: HTMLImageElement;
  render?: React.ReactElement;
  rotation?: number;
};

class FileUpload extends Component<FileUploadProps> {
  _notificationSystem: NotificationSystem | null = null;
  dropZones: Record<string, DropZoneItem>;
  /** Coalesces rapid rotate clicks into one backend save per slot (last rotation wins). */
  _rotateSave = createTrailingDebounce();
  /** Monotonic token per slot so an out-of-order toBlob from a superseded rotation is ignored. */
  _rotateSeq: Record<string, number> = {};

  constructor(props: FileUploadProps) {
    super(props);
    this._notificationSystem = null;
    this.dropZones = {};
  }

  componentWillUnmount() {
    // Persist a rotation the doctor made just before leaving the step — never lose the last one.
    this._rotateSave.flush();
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "file_upload");
  }

  showFileInputThumbnail() {
    const { file } = this.props;
    const dz_width = "180px";
    const dz_height = "72px";

    if (file && file.user_filename) {
      const file_extension = file.user_filename.split(".").slice(-1)[0].toUpperCase();
      const imgUrl = "/img/upload_placeholders/THUMBNAIL_" + file_extension + ".svg" + `?${Number(new Date())}`;
      // if (file.img) imgUrl = file.img.preview;
      return (
        <div className="flex-row" style={{ marginBottom: "16px" }}>
          <div className="col col--shrink">
            <div data-matomo-mask data-hj-suppress className="FileUpload">
              <Dropzone
                id={this.props.id}
                multiple={false}
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "let",
                  justifyContent: "left",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div
                  data-matomo-mask
                  data-hj-suppress
                  style={{
                    width: dz_width,
                    height: dz_height,
                    backgroundImage: `url(${imgUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    overflow: "hidden",
                    float: "left",
                  }}
                />
              </Dropzone>
              <NotificationSystem ref={(notificationSystem) => (this._notificationSystem = notificationSystem)} />
            </div>
          </div>

          <div data-matomo-mask data-hj-suppress className="col" style={{ display: "flex", alignItems: "center" }}>
            {file.user_filename}
          </div>
        </div>
      );
    } else {
      const imgUrl = "/img/upload_placeholders/recipe.svg";
      return (
        <div data-matomo-mask data-hj-suppress className="FileUpload">
          <Dropzone
            id={this.props.id}
            multiple={this.props.multiple ?? false}
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onDrop={(accepted) => {
              const extensionOf = (file: File) => file.name.split(".").pop()?.toLowerCase() ?? "";
              const supported = (accepted ?? []).filter((file) => /^(jpg|jpeg|png|stl|pdf|ortho|mp4)$/.test(extensionOf(file)));
              const heic = (accepted ?? []).filter(isHeicFile);

              if (heic.length === 0 && supported.length > 0) {
                this.handleDropped(supported, this.props.id);
              } else if (heic.length > 0) {
                void convertHeicFiles(heic)
                  .then((converted) => this.handleDropped([...supported, ...converted], this.props.id))
                  .catch(() => {
                    this._notificationSystem.addNotification({
                      message: this.props.locale === "en" ? en["notify.upload.E_UNSUPPORTED_FORMAT_EXT"] : ru["notify.upload.E_UNSUPPORTED_FORMAT_EXT"],
                      level: "error",
                    });
                  });
              } else {
                this._notificationSystem.addNotification({
                  message: this.props.locale === "en" ? en["notify.upload.E_UNSUPPORTED_FORMAT_EXT"] : ru["notify.upload.E_UNSUPPORTED_FORMAT_EXT"],
                  level: "error",
                });
              }
            }}
          >
            <button
              data-matomo-mask
              data-hj-suppress
              type="button"
              style={{
                backgroundColor: "transparent",
                border: "none",
                width: dz_width,
                height: dz_height,
                backgroundImage: `url(${imgUrl})`,
                backgroundSize: "cover",
                overflow: "hidden",
              }}
              id={`upload-file-btn-${this.props.id}`}
              aria-labelledby={clsx(this.props.ariaLabelledBy, `upload-file-btn-${this.props.id}`)}
            >
              <span style={{ position: "absolute", top: 7, left: 12, maxWidth: 10 }}>
                <FormattedMessage id="UPLOAD_FILE_BTN" />
              </span>
            </button>
          </Dropzone>

          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "-1px",
              width: "100%",
              height: "5px",
              overflow: "hidden",
            }}
          >
            <div
              id={`__PROGRESSBAR__-${this.props.id}`}
              className="__PROGRESSBAR__"
              style={{
                position: "absolute",
                width: "0",
                maxWidth: "100%",
                height: "100%",
                backgroundColor: this.props.progressBarColor ?? "#00858A",
                transition: "width 0.1s",
              }}
            />
          </div>

          <NotificationSystem ref={(notificationSystem) => (this._notificationSystem = notificationSystem)} />
        </div>
      );
    }
  }

  handleDropped(accepted: File[], id: FileUploadProps["id"], clbk = () => this.props.onFileUploadEnd?.()) {
    const batch = this.props.multiple && this.props.nextBatchId ? accepted : accepted.slice(0, 1);

    batch.forEach((file, offset) => {
      const targetId = offset === 0 ? this.props.id : (this.props.nextBatchId?.(offset) ?? this.props.id);

      if (id) {
        $(`#__PROGRESSBAR__-${targetId}`).css({ width: "0%" });
      }

      this.props.handleDropped(file, this.props.patient_id, this.props.semantics, targetId, this.props.correctionNum, clbk, this.props.maxFileSize);
    });
  }

  enableDropZone(accepted: FileWithPreview, id: string) {
    if ((this.dropZones[id] || {}).canvas) {
      delete this.dropZones[id].canvas;
    }
    if (((((this.props || {}).patient || {}).media || {}).required_images || {})[id]) {
      this.props.patient.media.required_images[id] = false;
    }
    this.dropZones[id] = {};
    const item = this.dropZones[id];
    item.canvas = document.createElement("canvas");
    item.ctx = item.canvas.getContext("2d");
    item.name = accepted[0].name;
    item.image = new Image();
    item.image.src = accepted[0].preview;
    item.image.onload = () => {
      if (item.image.width > item.image.height) {
        if (item.image.width > MAX_BROAD_SIDE_WIDTH) {
          item.image.height = (MAX_BROAD_SIDE_WIDTH / item.image.width) * item.image.height;
          item.image.width = MAX_BROAD_SIDE_WIDTH;
        }
      } else {
        if (item.image.height > MAX_BROAD_SIDE_WIDTH) {
          item.image.width = (MAX_BROAD_SIDE_WIDTH / item.image.height) * item.image.width;
          item.image.height = MAX_BROAD_SIDE_WIDTH;
        }
      }
      // Supported browsers apply EXIF orientation while decoding. Applying it again here would rotate
      // the canvas twice; zero preserves the decoded orientation without affecting manual rotation.
      this.rotate(id, 0);
    };
  }

  getOldImg(id: FileUploadProps["id"]): TCorrectionMediaView | undefined {
    const { patient } = this.props;

    if (!isPatient(patient) || this.props.ignoreExistingMedia) {
      return undefined;
    } else if (this.props.correctionNum) {
      return hasUnfilledCorrection(patient) ? patient.media.correction_media[this.props.correctionNum]?.[id as TCorrectionMediaSemantics] : undefined;
    } else {
      return patient.media.required_images[id as TCorrectionMediaSemantics];
    }
  }

  /** Whether the slot shows a saved or newly dropped photo instead of its empty placeholder. */
  hasExistingPreview(id: FileUploadProps["id"]): boolean {
    return Boolean(this.dropZones[id]?.render) || Boolean(this.getOldImg(id));
  }

  showDropzonePreview(id: FileUploadProps["id"]) {
    const dz_width = `${DROP_ZONE_WIDTH}px`;
    const dz_height = `${DROP_ZONE_HEIGHT}px`;

    const oldImg = this.getOldImg(id);

    if (this.dropZones[id]?.render) {
      return this.dropZones[id]?.render;
    }

    if (oldImg && isPatient(this.props.patient)) {
      return (
        <div data-matomo-mask data-hj-suppress style={{ width: dz_width, height: dz_height, overflow: "hidden", position: "relative" }}>
          <Img
            loadingFallback={<Skeleton width={dz_width} height={dz_height} style={{ lineHeight: "unset" }} />}
            errorFallback={null}
            src={`${API_PATIENT_IMAGE(this.props.patient.patient_id, "")}${oldImg.thumbnail + `?${Number(new Date())}`}`}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
              display: "block",
              border: "solid silver 1px",
            }}
          />
        </div>
      );
    }

    const redesignedPlaceholderUrl = redesignedPhotoPlaceholder(this.props.semantics);
    const redesignedPlaceholder = redesignedPlaceholderUrl !== null;

    // Empty redesign cards use the SVG's aspect ratio; uploaded photos keep the standard drop-zone size.
    const redesignedDzWidth = redesignedPlaceholder ? "314px" : dz_width;
    const redesignedDzHeight = redesignedPlaceholder ? "172px" : dz_height;

    // Legacy semantics still resolve through the static placeholder path.
    const placeholderUrl = redesignedPlaceholderUrl ?? `/img/upload_placeholders/${this.props.semantics}.svg`;

    return (
      <button
        data-matomo-mask
        data-hj-suppress
        type="button"
        style={{
          backgroundColor: "transparent",
          // Redesigned cards include their frame in the SVG.
          border: redesignedPlaceholder ? "none" : "solid silver 1px",
          width: redesignedDzWidth,
          height: redesignedDzHeight,
          // Safari renders these SVGs more sharply as images than as CSS backgrounds.
          backgroundImage: redesignedPlaceholder ? undefined : `url(${placeholderUrl})`,
          backgroundSize: redesignedPlaceholder ? undefined : "cover",
          backgroundRepeat: redesignedPlaceholder ? undefined : "no-repeat",
          backgroundPosition: redesignedPlaceholder ? undefined : "center",
          overflow: "hidden",
        }}
        id={`preview-btn-${this.props.id}`}
        aria-labelledby={clsx(this.props.ariaLabelledBy, `preview-btn-${this.props.id}`)}
      >
        {redesignedPlaceholder ? <img src={placeholderUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} /> : null}
        {/* Redesigned cards bake the "Загрузите фото" label into the SVG — don't render a second caption. */}
        {redesignedPlaceholder ? null : (
          <span style={{ position: "absolute", bottom: 5, left: 10 }}>
            <FormattedMessage id="UPLOAD_BTN" />
          </span>
        )}
      </button>
    );
  }

  rotate(id: FileUploadProps["id"], _rotation: number) {
    const item = this.dropZones[id];
    item.rotation = _rotation;
    if (item.rotation === 4) {
      item.rotation = 0;
    }
    const { rotation, image, canvas, ctx, name } = item;
    let _width = image.width;
    let _height = image.height;
    if (rotation % 2) {
      _width = image.height;
      _height = image.width;
    }
    if (_width > _height) {
      _width = (DROP_ZONE_HEIGHT / _height) * _width;
      _height = DROP_ZONE_HEIGHT;
    } else {
      _height = (_height / _width) * DROP_ZONE_WIDTH;
      _width = DROP_ZONE_WIDTH;
    }
    canvas.width = DROP_ZONE_WIDTH;
    canvas.height = DROP_ZONE_HEIGHT;
    ctx.clearRect(0, 0, _width, _height);
    if (rotation === 2) {
      ctx.translate(_width, _height);
    } else if (rotation === 1) {
      ctx.translate(_width, 0);
    } else if (rotation === 3) {
      ctx.translate(0, _height);
    }
    ctx.rotate(Math.PI * 0.5 * rotation);
    if (image.width > image.height) {
      [
        () => ctx.drawImage(image, (DROP_ZONE_WIDTH - _width) * 0.5, 0, _width, _height),
        () => ctx.drawImage(image, (DROP_ZONE_HEIGHT - _height) * 0.5, 0, _height, _width),
        () => ctx.drawImage(image, (_width - DROP_ZONE_WIDTH) * 0.5, 0, _width, _height),
        () => ctx.drawImage(image, (_height - DROP_ZONE_HEIGHT) * 0.5, 0, _height, _width),
      ][rotation]();
    } else {
      [
        () => ctx.drawImage(image, 0, (DROP_ZONE_HEIGHT - _height) * 0.5, _width, _height),
        () => ctx.drawImage(image, 0, (_width - DROP_ZONE_WIDTH) * 0.5, _height, _width),
        () => ctx.drawImage(image, 0, (_height - DROP_ZONE_HEIGHT) * 0.5, _width, _height),
        () => ctx.drawImage(image, 0, (DROP_ZONE_WIDTH - _width) * 0.5, _height, _width),
      ][rotation]();
    }
    const dataURL = canvas.toDataURL("image/jpeg", 1);

    canvas.width = image.width;
    canvas.height = image.height;
    if (rotation % 2) {
      canvas.width = image.height;
      canvas.height = image.width;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (rotation === 2) {
      ctx.translate(canvas.width, canvas.height);
    } else if (rotation === 1) {
      ctx.translate(canvas.width, 0);
    } else if (rotation === 3) {
      ctx.translate(0, canvas.height);
    }
    ctx.rotate(Math.PI * 0.5 * rotation);
    if (image.width > image.height) {
      [
        () => ctx.drawImage(image, 0, 0, canvas.width, canvas.height),
        () => ctx.drawImage(image, 0, 0, canvas.height, canvas.width),
        () => ctx.drawImage(image, 0, 0, canvas.width, canvas.height),
        () => ctx.drawImage(image, 0, 0, canvas.height, canvas.width),
      ][rotation]();
    } else {
      [
        () => ctx.drawImage(image, 0, 0, canvas.width, canvas.height),
        () => ctx.drawImage(image, 0, 0, canvas.height, canvas.width),
        () => ctx.drawImage(image, 0, 0, canvas.width, canvas.height),
        () => ctx.drawImage(image, 0, 0, canvas.height, canvas.width),
      ][rotation]();
    }

    // Update the preview immediately; the backend save is debounced across repeated rotations.
    item.render = <img data-matomo-mask data-hj-suppress src={dataURL} />;
    this.forceUpdate();

    const seq = (this._rotateSeq[id] = (this._rotateSeq[id] ?? 0) + 1);
    const isInitialPlacement = seq === 1; // the EXIF auto-orient on drop — save promptly
    canvas.toBlob(
      (blob) => {
        // A newer rotation already superseded this one — its blob is what should be saved.
        if (seq !== this._rotateSeq[id]) return;
        const file = new File([blob], name, { type: "image/jpeg" });
        const key = String(id);
        this._rotateSave.schedule(key, () => this.handleDropped([file], id));
        // First upload of a slot fires immediately; only follow-up rotations coalesce.
        if (isInitialPlacement) this._rotateSave.flush(key);
      },
      "image/jpeg",
      1,
    );
  }

  render() {
    if (this.props.preview) {
      // The empty redesign card is larger than the standard uploaded-photo preview.
      const showsEmptyRedesignedPlaceholder = redesignedPhotoPlaceholder(this.props.semantics) !== null && !this.hasExistingPreview(this.props.id);

      return (
        <div
          style={{
            position: "relative",
            width: showsEmptyRedesignedPlaceholder ? "314px" : "180px",
            height: showsEmptyRedesignedPlaceholder ? "172px" : "135px",
            backgroundColor: "white",
            margin: "5px",
          }}
        >
          <Dropzone
            id={this.props.id}
            multiple={false}
            accept=".jpg, .jpeg, .heic, .heif"
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onDrop={(accepted) => {
              if (accepted && accepted[0] && accepted[0].type === "image/jpeg") {
                this.enableDropZone(accepted, this.props.id);
              } else if (accepted && accepted[0] && isHeicFile(accepted[0])) {
                void convertHeicFiles(accepted)
                  .then((converted) => this.enableDropZone(converted, this.props.id))
                  .catch(() => {
                    this._notificationSystem.addNotification({
                      message: this.props.locale === "en" ? en["notify.upload.E_UNSUPPORTED_FORMAT"] : ru["notify.upload.E_UNSUPPORTED_FORMAT"],
                      level: "error",
                    });
                  });
              } else {
                this._notificationSystem.addNotification({
                  message: this.props.locale === "en" ? en["notify.upload.E_UNSUPPORTED_FORMAT"] : ru["notify.upload.E_UNSUPPORTED_FORMAT"],
                  level: "error",
                });
              }
            }}
          >
            {this.showDropzonePreview(this.props.id)}
          </Dropzone>

          {/* Counter-clockwise is on the left and clockwise on the right, matching the classifier cards. */}
          {!this.dropZones[this.props.id] || (
            <button
              type="button"
              id={`__ROTATE_BUTTON__-${this.props.id}`}
              aria-label={String(this.props.locale === "en" ? en["photo_classifier.rotate_left"] : ru["photo_classifier.rotate_left"])}
              style={{
                position: "absolute",
                left: "-1px",
                top: "-1px",
                width: "32px",
                height: "32px",
                backgroundColor: "rgba(255,255,255,0.75)",
                backgroundImage: `url(${rotateLeft})`,
                cursor: "pointer",
                border: "none",
              }}
              onClick={() => this.rotate(this.props.id, (this.dropZones[this.props.id].rotation + 3) % 4)}
            />
          )}

          {!this.dropZones[this.props.id] || (
            <button
              type="button"
              id={`__ROTATE_BUTTON__-${this.props.id}`}
              aria-label={String(this.props.locale === "en" ? en["photo_classifier.rotate_right"] : ru["photo_classifier.rotate_right"])}
              style={{
                position: "absolute",
                right: "-1px",
                top: "-1px",
                width: "32px",
                height: "32px",
                backgroundColor: "rgba(255,255,255,0.75)",
                backgroundImage: `url(${rotateRight})`,
                cursor: "pointer",
                border: "none",
              }}
              onClick={() => this.rotate(this.props.id, (this.dropZones[this.props.id].rotation + 1) % 4)}
            />
          )}

          {!this.dropZones[this.props.id] || (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "-1px",
                width: "100%",
                height: "5px",
                overflow: "hidden",
              }}
            >
              <div
                id={`__PROGRESSBAR__-${this.props.id}`}
                style={{
                  position: "absolute",
                  width: "0",
                  maxWidth: "100%",
                  height: "100%",
                  backgroundColor: this.props.progressBarColor ?? "#00858A",
                  transition: "width 0.1s",
                }}
              />
            </div>
          )}

          <NotificationSystem ref={(notificationSystem) => (this._notificationSystem = notificationSystem)} />
        </div>
      );
    } else {
      return this.showFileInputThumbnail();
    }
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(FileUpload);
